import { Checkbox, FormControlLabel, Paper, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import DownloadIcon from "@mui/icons-material/Download";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MOBILE_WIDTH } from "../App";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { AppDialog } from "../components/AppDialog";
import { AppToolbar, CURRENT_YEAR_MAX_DATE, DatePickerComponent, DatePickerWrapper, MAX_DATE, MIN_DATE } from "../components/AppToolbar";
import { ButtonComponent } from "../components/ButtonComponent";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import { SelectComponent } from "../components/SelectComponent";
import { SimpleAutoComplete } from "../components/SimpleAutoComplete";
import { WorklogModalCreation } from "../components/Worklog/WorklogModalCreation";
import { WorklogSlackReport } from "../components/Worklog/WorklogSlackReport";
import TableBasedCrud, { ActionsMenuEntry } from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import { ColumnSpec } from "../external/ensolvers-core-frontend-mui/components/Common/GenericTable";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import DateUtils from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import useWindowSize from "../hooks/useWindowSize";
import useWorkLogList, { WORKLOG_PAGE_SIZE } from "../hooks/useWorkLogList";
import WorkLog from "../models/WorkLog";
import { WorlogWeeksDaysFilters } from "../utils/enums/filters/WorklogFilters";
import { generateReportAndDownload } from "../utils/FileUtils";
import OpsDateUtils from "../utils/OpsDateUtils";
import RoleUtils from "../utils/RoleUtils";
import WorklogsUtils from "../utils/WorklogsUtils";
import useTableHeight from "../hooks/useTableHeight";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import NumberUtils from "../utils/NumberUtils";
import WorklogReportUtils from "../utils/WorklogReportUtils";
import { workLogCategory } from "../utils/enums/WorkLogCategory";
import { QuickWorklogTextInput } from "../components/Worklog/WorklogTextInput";
import { QuickEditRow } from "../components/Worklog/QuickEditRow";
import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";

import MoreTimeIcon from "@mui/icons-material/MoreTime";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import SendAndArchiveOutlinedIcon from "@mui/icons-material/SendAndArchiveOutlined";
import SendIcon from "@mui/icons-material/Send";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import BoltIcon from "@mui/icons-material/Bolt";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import { Box, Button } from "@mui/material";
import { filtersBarStyle, NUMERIC_SX, Token } from "../theme";
import { workLogRowStyles } from "./workLogRowStyles";
import { WorklogTotals } from "../components/Worklog/WorklogTotals";
import useLocalStorage, { STORAGE_KEYS } from "../hooks/useLocalStorage";
import { WorklogCalendar } from "../components/Worklog/WorklogCalendar";
import { WORKLOG_SKELETON_ROWS, WorklogCalendarSkeleton, WorklogTableSkeleton } from "../components/Worklog/WorklogSkeleton";
import { WorklogWeekApproval } from "../components/Worklog/WorklogWeekApproval";
import useWorklogWeekApproval from "../hooks/useWorklogWeekApproval";
import { ViewToggle } from "../components/Worklog/ViewToggle";
import { Theme } from "@mui/material/styles";
import { FIELD_LABEL_STYLE, ITEM_HEIGHT, TRACK_HEIGHT } from "../components/SegmentedControl";

export default function WorkLogList() {
  const { t } = useTranslation();

  const {
    user,
    assignation,
    currPosition,
    workLog,
    setWorkLog,
    formDialog,
    setFormDialog,
    isEditMode,
    setIsEditMode,
    assignations,
    isLoading,
    isFetchingWorklogs,
    forceUpdateOn,
    selectedWorkLog,
    setSelectedWorkLog,
    unified,
    setUnified,
    slackReportDialog,
    setSlackReportDialog,
    holidays,
    userTimeoffs,
    selectedDate,
    isManagerOfProject,
    isAssignationActive,
    handleOnDeleteWorkLog,
    saveWorkLog,
    refreshWorklogs,
    onDataFetch,
    selectAssignation,
    userExternalId,
    handleTimeoffs,
    onlyWeekDays,
    setOnlyWeekDays,
    selectedAssignation,
    changeDate,
    paramAssignationExternalId,
    queryYear,
    queryMonth,
    results,
    showOnlyMissing,
    setShowOnlyMissing,
  } = useWorkLogList();
  const classes = useStyles();

  const canApproveWeeks = !!user && (RoleUtils.isAdmin() || (RoleUtils.isManager() && isManagerOfProject));
  const weekApproval = useWorklogWeekApproval({
    rows: results,
    isAssignationActive,
    assignationExternalId: paramAssignationExternalId || selectedAssignation?.externalId || undefined,
    projectExternalId:
      results.find((row) => row.projectExternalId)?.projectExternalId ||
      currPosition?.projectExternalId ||
      assignation?.position?.projectExternalId,
    enabled: canApproveWeeks,
    refreshWorklogs,
  });

  const [isOpen, setIsOpen] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const showXlsxDownload = RoleUtils.hasSomeRoleExceptContractor();
  const [focusField, setFocusField] = useState<string | null>(null);

  // Save the date of the worklog
  const [quickEditWorkLog, setQuickEditWorkLog] = useState<WorkLog | null>(null);
  const [shouldFocus, setShouldFocus] = useState(false);
  const workLogListRef = useRef<WorkLog[]>([]);
  const [enterPressed, setEnterPressed] = useState(false);
  const [view, setView] = useLocalStorage<"table" | "calendar">(STORAGE_KEYS.WORKLOG_VIEW, "table");

  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();

  const loadWorklogs = (pageNumber: number) => {
    const result = onDataFetch(pageNumber, WORKLOG_PAGE_SIZE);

    // We use this result to be able to copy workLogs
    if (result) {
      result.then((data) => {
        workLogListRef.current = data.content;
      });
    }
    return result;
  };

  const shortAssignationLabel = (label: string) => label.split(" - ").slice(0, 2).join(" - ");

  // The table is what pulls the data, through onDataFetch. In calendar view it never mounts,
  // so without this the calendar comes back empty whenever it is the view stored from last time.
  // Year, month and the owner's country are what build the query key. The effect waits for `user`
  // so the first request uses that country. Weekday and missing-day filters must not start another request.
  useEffect(() => {
    if (view !== "calendar") return;
    loadWorklogs(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, forceUpdateOn, assignation, selectedAssignation, paramAssignationExternalId, queryYear, queryMonth, userExternalId, user]);

  useEffect(() => {
    if (shouldFocus && firstInputRef.current) {
      firstInputRef.current.select();
      setShouldFocus(false);
    }
  }, [shouldFocus]);

  useEffect(() => {
    if (enterPressed) {
      handleOnQuickEditSave(quickEditWorkLog!, true);
    }
    setEnterPressed(false);
  }, [quickEditWorkLog]);

  const handleDateChange = (date: Date | null) => {
    if (date && date !== selectedDate) {
      changeDate(date);
    }
  };

  useEffect(() => {
    setQuickEditWorkLog(null);
  }, [formDialog]);

  const canModifyWorklog = (): boolean => {
    return (
      RoleUtils.isAdmin() ||
      userExternalId === undefined ||
      isManagerOfProject ||
      userExternalId === user?.externalId! ||
      userExternalId === user?.coreUserExternalId ||
      selectedAssignation !== undefined
    );
  };

  const isBlockedByApprovedWeek = (instance: WorkLog): boolean => {
    if (!canApproveWeeks || !weekApproval.isDayApproved(instance)) return false;
    NotificationService.info('This week is approved. Untick it in "Approved weeks" to edit its days.');
    return true;
  };

  const getAdditionalActions = (instance: WorkLog, shortList: boolean): ActionsMenuEntryIcons[] => {
    const isNew = !instance.externalId;

    if (!shortList) {
      return [
        {
          title: t("Send External Report to Slack"),
          action: async () => {
            const report = await WorklogsUtils.getExternalReport(instance);
            setSlackReportDialog({ reportMsg: report!, worklog: instance, channel: "EXTERNAL" });
          },
          icon: <SendIcon />,
        },
        {
          title: t("Copy External Report"),
          action: async () => {
            try {
              const report = await WorklogsUtils.getExternalReport(instance);
              navigator.clipboard.writeText(report!);
              NotificationService.info("External report copied to clipboard");
            } catch (error) {
              NotificationService.error("Unexpected error copying external report to clipboard");
            }
          },
          icon: <FileCopyIcon />,
        },
      ];
    }

    const actions =
      !isNew && selectedAssignation
        ? [
            {
              title: t("Edit"),
              action: () => {
                if (isBlockedByApprovedWeek(instance)) return;
                instance.assignationName = selectedAssignation.label;
                instance.assignationExternalId = selectedAssignation.externalId;
                setIsEditMode(true);
                setWorkLog(instance);
                setFormDialog(true);
              },
              icon: <EditIcon />,
            },
            {
              title: t("Copy Internal Report"),
              action: () => {
                try {
                  navigator.clipboard.writeText(WorklogsUtils.getInternalReport(instance));
                  NotificationService.info("Internal report copied to clipboard");
                } catch (error) {
                  NotificationService.error("Unexpected error copying internal report to clipboard");
                }
              },
              icon: <FileCopyOutlinedIcon />,
            },
            {
              title: t("Send Internal Report to Slack"),
              action: () => {
                setSlackReportDialog({
                  reportMsg: WorklogsUtils.getInternalReport(instance),
                  worklog: instance,
                  channel: "INTERNAL",
                });
              },
              icon: <SendAndArchiveOutlinedIcon />,
            },
          ]
        : isAssignationActive(instance) && selectedAssignation
          ? [
              {
                title: t("Add"),
                action: () => {
                  if (isBlockedByApprovedWeek(instance)) return;
                  // New Instance. Set Default Values
                  instance.externalId = undefined;
                  instance.hours = currPosition ? currPosition.externalHours : 8;
                  instance.internalHours = currPosition ? currPosition.internalHours : 8;
                  instance.adjustmentHours = 0;
                  instance.bonusHours = 0;
                  instance.assignationName = selectedAssignation.label;
                  instance.assignationExternalId = selectedAssignation.externalId;
                  instance.description = !canModifyWorklog() ? "PLACE HOLDER" : "";
                  instance.internalDescription = !canModifyWorklog() ? "PLACE HOLDER" : "";
                  handleTimeoffs(instance);
                  setIsEditMode(false);
                  setWorkLog(instance);
                  setFormDialog(true);
                },
                icon: <MoreTimeIcon />,
              },
            ]
          : [];

    if (!isNew && canModifyWorklog()) {
      return [
        {
          title: "Delete",
          action: () => {
            if (isBlockedByApprovedWeek(instance)) return;
            setSelectedWorkLog(instance);
          },
          icon: <DeleteOutlineIcon />,
        },
        ...actions,
      ];
    }

    return actions;
  };

  const isTimeOffDay = (instance: WorkLog) =>
    !!userTimeoffs?.some((timeoff) =>
      OpsDateUtils.isBetweenOrEqual(timeoff.shortStartDate, timeoff.shortEndDate, OpsDateUtils.toUTCWithSetHours(instance.shortDay)),
    );

  const openQuickEditFor = (instance: WorkLog, field: string = "description") => {
    if (isEditingRow(instance) || !isAssignationActive(instance)) return;
    if (isBlockedByApprovedWeek(instance)) return;

    let prefilled = { ...instance };

    if (!(instance.hours || instance.internalHours || instance.description)) {
      const previousWorkLog = workLogListRef.current.find((workLog) => workLog.shortDay.getDate() === instance.shortDay.getDate() - 1);

      prefilled = previousWorkLog?.description
        ? {
            ...prefilled,
            hours: previousWorkLog.hours,
            internalHours: previousWorkLog.internalHours,
            description: previousWorkLog.description,
            internalDescription: previousWorkLog.internalDescription,
          }
        : {
            ...prefilled,
            hours: currPosition ? currPosition.externalHours : 8,
            internalHours: currPosition ? currPosition.internalHours : 8,
          };
    }

    setQuickEditWorkLog(prefilled);
    setFocusField(field);
  };

  // The inline editor only exists inside the table, so a click on the calendar has to take the user
  // back to it: on its own it just selected the day and nothing visible happened.
  const handleCalendarDayClick = (instance: WorkLog) => {
    setView("table");
    openQuickEditFor(instance);
  };

  const isMissingDay = (instance: WorkLog) =>
    !instance.externalId &&
    !instance.isWeekend &&
    !instance.isHoliday &&
    !instance.isPaused &&
    !isTimeOffDay(instance) &&
    isAssignationActive(instance);

  const stylesForRowIfCondition = (instance: WorkLog) =>
    workLogRowStyles(instance, {
      expectedExternalHours: currPosition ? currPosition.externalHours : 8,
      expectedInternalHours: currPosition ? currPosition.internalHours : 8,
      isTimeOff: isTimeOffDay(instance),
      isAssignationActive: isAssignationActive(instance),
      isEndOfWorkWeek: onlyWeekDays === WorlogWeeksDaysFilters.WORKDAYS && instance.shortDay.getDay() === 5,
    });

  const renderAssignationAutocomplete = (
    <>
      <SimpleAutoComplete
        label="Assignation"
        inlineLabel
        items={assignations.map((a) => ({ ...a, label: shortAssignationLabel(a.label) }))}
        value={
          selectedAssignation ? { ...selectedAssignation, label: shortAssignationLabel(selectedAssignation.label) } : EMPTY_AUTOCOMPLETE
        }
        onChange={(newValue) => {
          selectAssignation(assignations.find((a) => a.externalId === newValue.externalId) ?? EMPTY_AUTOCOMPLETE);
        }}
        // Flexible instead of a hard 400px floor: it takes the leftover room on wide screens and
        // gives it back on narrow ones, so the row holds together before it has to wrap.
        sx={{ flex: "1 1 220px", minWidth: 0, maxWidth: 300 }}
      />
    </>
  );

  const renderSecondaryFilters = (
    <>
      <SelectComponent
        label={"Day"}
        inlineLabel
        value={onlyWeekDays}
        setValue={(status: string) => setOnlyWeekDays(status)}
        options={Object.values(WorlogWeeksDaysFilters)}
        customStyle={{ minWidth: "12em" }}
        size="small"
      />
      <DatePickerWrapper>
        <DatePickerComponent
          className={classes.datePicker}
          views="month"
          label="Month"
          inlineLabel
          value={selectedDate}
          inputFormat="MMMM"
          onChange={handleDateChange}
          readOnly
          style={{ margin: "0", textAlignLast: "left", maxWidth: "13.2em" }}
        />
        <DatePickerComponent
          className={classes.datePicker}
          views="year"
          label="Year"
          inlineLabel
          value={selectedDate}
          inputFormat="yyyy"
          style={{ marginRight: "0", textAlignLast: "left", maxWidth: "10em" }}
          minDate={MIN_DATE}
          maxDate={MAX_DATE}
          onChange={handleDateChange}
          readOnly
        />
      </DatePickerWrapper>
      <Button
        variant={showOnlyMissing ? "contained" : "outlined"}
        size="small"
        startIcon={<ErrorOutlinedIcon />}
        onClick={() => {
          setShowOnlyMissing(!showOnlyMissing);
        }}
        sx={{
          borderRadius: "4px",
          backgroundColor: showOnlyMissing ? Token.actionPrimary : "transparent",
          color: showOnlyMissing ? Token.textOnAction : Token.textSecondary,
          border: `1px solid ${Token.borderDefault}`,
          textTransform: "none",
          fontSize: "12px",
          padding: "6px 12px",
          "&:hover": {
            backgroundColor: showOnlyMissing ? Token.actionPrimaryHover : Token.surfaceHover,
          },
        }}
      >
        Missing Only
      </Button>
    </>
  );

  const downloadDialogue = (
    <div className={classes.downloadSection}>
      <ViewToggle value={view} onChange={setView} />
      <Box style={{ display: "inline-flex", flexDirection: "column", flexShrink: 0 }}>
        <span style={FIELD_LABEL_STYLE}>Download</span>
        <Box style={{ display: "inline-flex", gap: 10 }}>
          <ButtonComponent
            outlined
            clearStyles
            style={{ height: ITEM_HEIGHT.md }}
            debounce
            icon={<DownloadIcon />}
            text="PDF"
            onClick={() =>
              generateReportAndDownload("PDF_REPORT_WORKLOG", selectedDate, selectedAssignation!, user?.externalId, unified).catch(
                (error: any) => {
                  if (error && error.message) {
                    NotificationService.error(error.message);
                  } else {
                    NotificationService.error("Unexpected error downloading report");
                  }
                },
              )
            }
          />
          {showXlsxDownload && (
            <ButtonComponent
              outlined
              clearStyles
              style={{ height: ITEM_HEIGHT.md }}
              debounce
              icon={<DownloadIcon />}
              text="XLSX"
              onClick={() => {
                generateReportAndDownload("XLSX_REPORT_WORKLOG", selectedDate, selectedAssignation!, user?.externalId, unified).catch(
                  (error: any) => {
                    if (error && error.message) {
                      NotificationService.error(error.message);
                    } else {
                      NotificationService.error("Unexpected error downloading report");
                    }
                  },
                );
              }}
            />
          )}
          {assignations.length > 1 && (
            <FormControlLabel
              label="Unified"
              control={<Checkbox checked={unified} onChange={(e) => setUnified(e.target.checked)} color="default" />}
            />
          )}
          {/*
          TODO: "Fill week" is a possible addition. Same thing exists in the dashboard's QuickLogTile, we could reuse that logic to implement this.
        */}
          {/* <ButtonComponent
          outlined
          clearStyles
          debounce
          sx={{
            backgroundColor: Token.actionPrimary,
            color: Token.textOnAction,
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 600,
            padding: "6px 12px !important",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: Token.actionPrimaryHover,
            },
          }}
          icon={<BoltIcon />}
          text="Fill week"
          onClick={() => console.log("to do: fill week")}
        /> */}
        </Box>
      </Box>
    </div>
  );

  const renderFilters = () => {
    return isMobileWidth() ? (
      <div className={classes.container}>
        {renderAssignationAutocomplete}
        <FilterDialogButton text="Filters & downloads" isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    ) : (
      <div className={classes.container}>
        <Paper className={classes.panel}>
          <div className={classes.filtersGroup}>
            {renderAssignationAutocomplete}
            {renderSecondaryFilters}
          </div>
          <WorklogTotals
            className={classes.totals}
            rows={results}
            expectedExternalHours={currPosition ? currPosition.externalHours : 8}
            isTimeOff={isTimeOffDay}
            isAssignationActive={isAssignationActive}
          />
        </Paper>
      </div>
    );
  };

  const renderDialogFilters = () => {
    return (
      <div className={classes.mobileFilters}>
        {renderSecondaryFilters}
        {downloadDialogue}
      </div>
    );
  };

  const handleOnQuickEditSave = async (quickEditWorkLogCreated: WorkLog, enterKeyDown?: boolean, isDayOff?: boolean) => {
    let workLogCreated: WorkLog = {
      ...quickEditWorkLogCreated,
      assignationExternalId: selectedAssignation.externalId,
      assignationName: selectedAssignation.label,
      category: isDayOff || quickEditWorkLogCreated.category == workLogCategory.DAY_OFF ? workLogCategory.DAY_OFF : workLogCategory.REGULAR,
      description: isDayOff ? "Day Off" : quickEditWorkLog?.description,
      internalDescription: isDayOff ? "Day Off" : quickEditWorkLog?.internalDescription,
      hours: isDayOff ? 0 : quickEditWorkLogCreated.hours,
      internalHours: isDayOff ? 0 : quickEditWorkLogCreated.internalHours,
      details: quickEditWorkLog?.details,
    };

    if (!workLogCreated.description || workLogCreated?.description!.trim().length === 0) {
      NotificationService.error("Description cannot be empty");
      return;
    }

    if (NumberUtils.hasDecimals(workLogCreated?.hours) || NumberUtils.hasDecimals(workLogCreated?.internalHours)) {
      NotificationService.error("Hours cannot have decimals");
      setQuickEditWorkLog(quickEditWorkLogCreated);
      return;
    }

    if (workLogCreated.adjustmentHours != null && !WorklogReportUtils.adjustmentHoursIsValid(workLogCreated)) {
      setQuickEditWorkLog(quickEditWorkLogCreated);
      return;
    }

    if (workLogCreated.bonusHours != null && !WorklogReportUtils.bonusHoursIsValid(workLogCreated, currPosition)) {
      setQuickEditWorkLog(quickEditWorkLogCreated);
      return;
    }

    // if the worklog has an externalId it means we're editing an existing one
    if (!quickEditWorkLogCreated.externalId) {
      quickEditWorkLogCreated.shortDay = OpsDateUtils.toUTCWithSetHours(quickEditWorkLogCreated.shortDay);
      workLogCreated.shortDay = quickEditWorkLogCreated.shortDay;
    }

    try {
      await saveWorkLog(workLogCreated);
    } catch {
      return;
    }

    // If the user pressed enter, we want to move to the next row
    if (enterKeyDown) {
      const currentDate = workLogCreated.shortDay.getDate();

      const nextDate = results.find((w) => w.shortDay.getDate() > currentDate && !w.isWeekend);

      if (nextDate) {
        setFocusField(null);
        setQuickEditWorkLog({
          ...nextDate,
          hours: nextDate?.hours || workLogCreated.hours,
          internalHours: nextDate?.internalHours || workLogCreated.internalHours,
          description: nextDate?.description || (isDayOff ? "" : workLogCreated.description),
          internalDescription: nextDate?.internalDescription || (isDayOff ? "" : workLogCreated.internalDescription),
          shortDay: new Date(nextDate.shortDay),
        });
      } else {
        setQuickEditWorkLog(null);
      }
      setShouldFocus(true);
      return;
    }

    setQuickEditWorkLog(null);
  };

  const handleFieldUpdate = (fieldName: string, newValue: string) => {
    if (quickEditWorkLog) {
      setQuickEditWorkLog({
        ...quickEditWorkLog,
        [fieldName]: newValue,
      });
    }
  };

  const handleInternalDescriptionFocus = (fieldName: string) => {
    if (fieldName === "internalDescription" && quickEditWorkLog && !quickEditWorkLog.internalDescription) {
      setQuickEditWorkLog({
        ...quickEditWorkLog,
        internalDescription: quickEditWorkLog.description,
      });
    }
  };

  const isEditingRow = (instance: WorkLog): boolean => {
    return Boolean(
      (quickEditWorkLog?.externalId && quickEditWorkLog?.externalId === instance.externalId) ||
      quickEditWorkLog?.shortDay.getDate() === instance.shortDay.getDate(),
    );
  };

  const handleEnterKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleOnQuickEditSave(quickEditWorkLog!, true);
    }
  };

  const tableColumns: (WorkLogColumn | null)[] = [
    {
      header: t("Date"),
      width: 8.5,
      content: (instance) => (
        <span
          id={`day-${instance.shortDay.getDate ? instance.shortDay.getDate() : new Date(instance.shortDay).getDate()}`}
          style={{ fontFamily: NUMERIC_SX.fontFamily, fontVariantNumeric: NUMERIC_SX.fontVariantNumeric }}
        >
          {DateUtils.formatAsDDDMMM(instance?.shortDay)}
        </span>
      ),
    },
    ...(user && userExternalId !== null && AuthService.getUser()?.externalId !== userExternalId
      ? [
          {
            header: t("Full Name"),
            width: 10,
            content: () => <>{`${user.firstName} ${user.lastName}`}</>,
          },
        ]
      : []),
    {
      header: t("Hours"),
      width: 4.5,
      content: (instance) => {
        if (isEditingRow(instance)) {
          return (
            <input
              type="number"
              className={classes.quickInput}
              autoFocus={focusField === "hours"}
              value={quickEditWorkLog!.hours}
              onChange={(e) => {
                setQuickEditWorkLog({
                  ...quickEditWorkLog!,
                  hours: Number(e.target.value),
                });
              }}
              onKeyDown={handleEnterKey}
            />
          );
        } else {
          return (
            <span
              className={classes.cellClickable}
              onClick={(e) => {
                e.stopPropagation();
                openQuickEditFor(instance, "hours");
              }}
            >
              {instance?.hours > 0 ? instance.hours : ""}
            </span>
          );
        }
      },
    },
    {
      header: t("Description"),
      content: (instance) => {
        if (isEditingRow(instance)) {
          return (
            <QuickWorklogTextInput
              reference={firstInputRef}
              className={classes.quickInputText}
              value={quickEditWorkLog!.description || ""}
              onFieldUpdate={(fieldName, newValue) => handleFieldUpdate(fieldName, newValue)}
              fieldName="description"
              onKeyDown={setEnterPressed}
              autofocus={focusField === "description"}
            />
          );
        } else {
          return (
            <span
              className={classes.cellText}
              onClick={(e) => {
                e.stopPropagation();
                openQuickEditFor(instance, "description");
              }}
            >
              {instance.description}
            </span>
          );
        }
      },
    },
    {
      header: t("Internal Hours"),
      width: 5.5,
      content: (instance) => {
        if (isEditingRow(instance)) {
          return (
            <input
              type="number"
              value={quickEditWorkLog!.internalHours}
              className={classes.quickInput}
              autoFocus={focusField === "internalHours"}
              onChange={(e) => {
                setQuickEditWorkLog({
                  ...quickEditWorkLog!,
                  internalHours: Number(e.target.value),
                });
              }}
              onKeyDown={handleEnterKey}
            />
          );
        } else {
          return (
            <span
              className={classes.cellClickable}
              onClick={(e) => {
                e.stopPropagation();
                openQuickEditFor(instance, "internalHours");
              }}
            >
              {instance?.internalHours > 0 ? instance.internalHours : ""}
            </span>
          );
        }
      },
    },
    RoleUtils.isAdminOrManager()
      ? {
          header: t("Adjustment Hours"),
          width: 6.5,
          content: (instance) => {
            if (isEditingRow(instance)) {
              return (
                <input
                  type="number"
                  className={classes.quickInput}
                  value={quickEditWorkLog!.adjustmentHours}
                  onChange={(e) => {
                    setQuickEditWorkLog({
                      ...quickEditWorkLog!,
                      adjustmentHours: Number(e.target.value),
                    });
                  }}
                  autoFocus={focusField === "adjustmentHours"}
                  onKeyDown={handleEnterKey}
                />
              );
            } else {
              return (
                <span
                  className={classes.cellClickable}
                  onClick={(e) => {
                    e.stopPropagation();
                    openQuickEditFor(instance, "adjustmentHours");
                  }}
                >
                  {instance?.adjustmentHours > 0 ? instance.adjustmentHours : ""}
                </span>
              );
            }
          },
        }
      : null,
    RoleUtils.isAdminOrManager()
      ? {
          header: t("Bonus Hours"),
          width: 5,
          content: (instance) => {
            if (isEditingRow(instance)) {
              return (
                <input
                  className={classes.quickInput}
                  type="number"
                  value={quickEditWorkLog!.bonusHours}
                  onChange={(e) => {
                    setQuickEditWorkLog({
                      ...quickEditWorkLog!,
                      bonusHours: Number(e.target.value),
                    });
                  }}
                  onKeyDown={handleEnterKey}
                  autoFocus={focusField === "bonusHours"}
                />
              );
            } else {
              return (
                <span
                  className={classes.cellClickable}
                  onClick={(e) => {
                    e.stopPropagation();
                    openQuickEditFor(instance, "bonusHours");
                  }}
                >
                  {instance?.bonusHours > 0 ? instance.bonusHours : ""}
                </span>
              );
            }
          },
        }
      : null,
    {
      header: t("Internal Description"),
      content: (instance) => {
        if (isEditingRow(instance)) {
          return (
            <QuickWorklogTextInput
              className={classes.quickInputText}
              value={quickEditWorkLog!.internalDescription || ""}
              onFieldUpdate={(fieldName, newValue) => handleFieldUpdate(fieldName, newValue)}
              onFocus={handleInternalDescriptionFocus}
              fieldName="internalDescription"
              onKeyDown={setEnterPressed}
              autofocus={focusField === "internalDescription"}
            />
          );
        } else {
          return (
            <span
              className={classes.cellText}
              onClick={(e) => {
                e.stopPropagation();
                openQuickEditFor(instance, "internalDescription");
              }}
            >
              {instance.internalDescription}
            </span>
          );
        }
      },
    },
    {
      header: t("Details"),
      width: 7,
      content: (instance) => {
        const details = WorklogsUtils.getWorklogDetails(instance);
        if (isEditingRow(instance)) {
          return (
            <QuickEditRow
              instance={instance}
              quickEditWorkLog={quickEditWorkLog || undefined}
              isEditingRow={isEditingRow}
              handleOnQuickEditSave={handleOnQuickEditSave}
              setQuickEditWorkLog={setQuickEditWorkLog}
              currPosition={currPosition}
              setShouldFocus={setShouldFocus}
              setFechUpdate={() => {
                void refreshWorklogs();
              }}
            />
          );
        } else {
          return instance.details && details.length > 0 ? <span className={classes.cellText}>{details.join(", ")}</span> : <></>;
        }
      },
    },
    {
      header: t("Actions"),
      width: 7.5,
      content: (instance) => {
        if (isMissingDay(instance)) {
          return (
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={(event) => {
                event.stopPropagation();
                openQuickEditFor(instance);
              }}
              sx={{
                minHeight: 26,
                height: 26,
                padding: "0 10px",
                fontSize: 12,
                whiteSpace: "nowrap",
              }}
            >
              {`+ ${t("Log")} ${currPosition ? currPosition.externalHours : 8} h`}
            </Button>
          );
        }

        const actions = getAdditionalActions(instance, true);
        return (
          <>
            {actions.map((action) => {
              return <ActionButton onClick={action.action} tooltip={action.title} icon={action.icon} />;
            })}
          </>
        );
      },
    },
  ];
  const visibleColumns = tableColumns.filter((column): column is WorkLogColumn => column !== null);
  const fixedColumnsWidth = visibleColumns.reduce((sum, column) => sum + (column.width ?? 0), 0);
  const tableSx = {
    // Below this the table scrolls sideways rather than squeezing the descriptions under 12rem each.
    "& table": { tableLayout: "fixed", minWidth: `${fixedColumnsWidth + 24}rem` },
    "& th": { whiteSpace: "normal" },
    ...Object.fromEntries(
      visibleColumns.map((column, index) => [`& th:nth-of-type(${index + 1})`, { width: column.width && `${column.width}rem` }]),
    ),
  };

  return (
    <>
      <AppContainerFullHeight
        bottomSectionRef={bottomSectionRef}
        topSection={
          <>
            <AppToolbar
              title={
                (user && userExternalId !== null && AuthService.getUser()?.externalId !== userExternalId
                  ? `${user.firstName} ${user.lastName} `
                  : "") + t("Work logs")
              }
              subtitle={
                selectedAssignation && currPosition ? `${selectedAssignation.label} • ${OpsDateUtils.formatMonthYear(selectedDate)}` : null
              }
              rightContent={isMobileWidth() ? undefined : downloadDialogue}
            >
              {renderFilters()}
            </AppToolbar>
            {canApproveWeeks && (
              <div style={{ marginTop: Token.cardPad }}>
                <WorklogWeekApproval
                  weeks={weekApproval.weeks}
                  busyWeekKey={weekApproval.busyWeekKey}
                  onToggle={weekApproval.toggleWeek}
                  lockPrompt={weekApproval.lockPrompt}
                  onDismissLockPrompt={weekApproval.dismissLockPrompt}
                  onConfirmOpenReport={weekApproval.confirmOpenReport}
                />
              </div>
            )}
          </>
        }
        bottomSection={
          <Box sx={{ borderRadius: 8 }}>
            {isLoading ? (
              view === "calendar" ? (
                <WorklogCalendarSkeleton />
              ) : (
                <WorklogTableSkeleton />
              )
            ) : view === "calendar" ? (
              isFetchingWorklogs && results.length === 0 ? (
                <WorklogCalendarSkeleton />
              ) : (
                <WorklogCalendar
                  rows={results}
                  isTimeOff={isTimeOffDay}
                  isAssignationActive={isAssignationActive}
                  onDayClick={handleCalendarDayClick}
                />
              )
            ) : (
              <Box sx={tableSx}>
                <TableBasedCrud<WorkLog>
                  loadingRowCount={WORKLOG_SKELETON_ROWS}
                  maxHeight={calculateMaxHeight(false)}
                  stickyHeader
                  disableAutoFetch
                  elementName="Work Log"
                  enablePagination={false}
                  denseTable={canModifyWorklog()}
                  forceUpdateOn={forceUpdateOn}
                  onElementClick={openQuickEditFor}
                  onDataFetch={(pageNumber) => loadWorklogs(pageNumber)}
                  renderDialogContent={() => <></>}
                  tableColumns={tableColumns}
                  buildAdditionalActions={(instance) => getAdditionalActions(instance, false)}
                  hideActions={true} // Hide default Actions column
                  stylesForRowIfCondition={stylesForRowIfCondition}
                />
              </Box>
            )}
          </Box>
        }
      />

      {!!workLog && (
        <WorklogModalCreation
          open={formDialog}
          setOpen={setFormDialog}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          workLog={workLog}
          setWorkLog={setWorkLog}
          holidays={holidays}
          paramUserExternalId={userExternalId!}
          user={user!}
          position={currPosition}
          onSaveWorkLog={saveWorkLog}
        />
      )}

      {/* Dialog to Confirm Slack Message */}
      {slackReportDialog && <WorklogSlackReport slackReportDialog={slackReportDialog} setSlackReportDialog={setSlackReportDialog} />}

      {/* Dialog to delete work logs */}
      <AppDialog
        open={!!selectedWorkLog}
        onClose={() => setSelectedWorkLog(undefined)}
        title={"Delete"}
        content={
          <Typography>
            {`The next worklog is going to be eliminated: ${DateUtils.formatAsDDDMMMYYY(selectedWorkLog?.shortDay!)} - ${
              selectedWorkLog?.description
            }.
            Do you want to confirm this action?`}
          </Typography>
        }
        buttons={[{ label: "Confirm delete", onClick: () => handleOnDeleteWorkLog() }]}
      />
      {/* Dialog to manage table filters on mobile view */}
      <FilterDialog title="Worklog" useCustomStyle={true} isOpen={isOpen} setIsOpen={setIsOpen}>
        {renderDialogFilters()}
      </FilterDialog>
    </>
  );
}

type WorkLogColumn = ColumnSpec<WorkLog> & { width?: number };

const quickInputBase = {
  height: Token.controlMd,
  padding: "0 0 0 0.5rem",
  borderRadius: Token.radiusControl,
  border: `1px solid ${Token.borderDefault}`,
  backgroundColor: Token.surfaceSunken,
  color: Token.textPrimary,
  fontFamily: "inherit",
  fontSize: Token.textBase,
  outline: "none",
  "&:focus": {
    borderColor: Token.borderFocus,
    boxShadow: `0 0 0 3px ${Token.focusRing}`,
  },
};

const useStyles = makeStyles<Theme, {}, string>(
  // @ts-ignore -- computed media-query keys are valid JSS but not StyleRules
  (theme) => ({
    // Two zones, like the bar in Worklog report: the filters wrap inside their own group and the
    // totals plus the downloads stay pinned to the right.
    panel: {
      ...filtersBarStyle(),
      flexWrap: "nowrap",
      alignItems: "center",
      gap: "16px",
      flexGrow: 1,
      minWidth: "300px", // Set a minimum width
    },
    filtersGroup: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "flex-end",
      gap: "10px",
      flex: "1 1 auto",
      minWidth: 0,
    },
    totals: {
      marginLeft: "auto",
      paddingLeft: "1.5rem",
      flex: "0 0 auto",
    },
    container: {
      position: "relative",
      width: "100%",
      display: "flex",
      columnGap: "10px",
    },
    datePicker: {
      marginRight: "0",
      width: "11rem !important",
    },
    mobileFilters: {
      display: "flex",
      padding: "25px 15px",
      flexDirection: "column",
      rowGap: "20px",
      "& > div": {
        width: "100% !important",
        margin: "0 !important",
      },
      "&> div:nth-child(2)": {
        display: "flex !important",
        flexDirection: "row",
        columnGap: "10px",
      },
    },
    cellClickable: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "100%",
    },
    cellText: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
      width: "100%",
      minWidth: 0,
      minHeight: "100%",
      padding: "4px 8px",
      whiteSpace: "normal",
      overflowWrap: "anywhere",
      textAlign: "center",
    },
    // Kept at controlMd so switching a row into edit mode does not make it taller than a read-only one,
    // and the focus ring is drawn with a shadow so it does not take up layout either.
    quickInput: {
      ...quickInputBase,
      maxWidth: "3rem",
    },
    quickInputText: {
      ...quickInputBase,
      boxSizing: "border-box",
      width: "calc(100% - 16px)",
    },
    // Sits next to the page title, so it reads as one row of actions rather than a stacked block.
    downloadSection: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      flexShrink: 0,
      whiteSpace: "nowrap",
      gap: "12px",
      marginBottom: "12px",
      "&> span": {
        fontSize: "10.5px",
        textTransform: "uppercase",
        letterSpacing: ".08em",
        fontWeight: 600,
        color: Token.textTertiary,
      },
      "&> .buttons": {
        display: "flex",
        columnGap: "1rem",
      },
      "&> label": {
        margin: "0",
      },
    },
    [theme.breakpoints.down(1000)]: {
      // Tight widths: the totals drop under the filters instead of squeezing the row.
      panel: {
        flexWrap: "wrap",
      },
    },
    [theme.breakpoints.down(MOBILE_WIDTH)]: {
      container: {
        display: "flex",
        flexDirection: "column",
        rowGap: "10px",
        width: "100%",
        "& div": {
          minWidth: "0",
        },
      },
    },
  }),
  { index: 1 },
);
